import {
  CertificateStatusEnum,
  ItemTypeEnum,
  isInstalmentWalletPaymentType,
  type ICertificateItem,
  type IInstalmentSchedule,
  type IInstalmentScheduleEntry,
  type InstalmentScheduleItemStatus,
  type IUserCertificate,
} from '@share/common-types';
import type { IntlShape } from 'react-intl';
import {
  certificateHasFailedInstalmentPayment,
  formatMoneyCurrency,
  instalmentScheduleHasFailedPayment,
} from '@share/utils';

import HotelIcon from '@assets/icons/certificates/wallet/hotel.svg';
import CondoIcon from '@assets/icons/certificates/wallet/condo.svg';
import CruiseIcon from '@assets/icons/certificates/wallet/cruise.svg';

import type {
  InstallmentProgressData,
  InstallmentProgressSegment,
  InstallmentSegmentUiStatus,
} from './components/installment-progress/types';

const ITEM_ICON_BY_TYPE: Record<string, string> = {
  [ItemTypeEnum.Hotel.toLowerCase()]: HotelIcon,
  [ItemTypeEnum.Condo.toLowerCase()]: CondoIcon,
  [ItemTypeEnum.Cruise.toLowerCase()]: CruiseIcon,
};

export function getWalletStatusClassName(status: CertificateStatusEnum): string {
  switch (status) {
    case CertificateStatusEnum.Canceled:
      return 'canceled';
    case CertificateStatusEnum.Active:
      return 'active';
    case CertificateStatusEnum.PartiallyRedeemed:
      return 'partiallyRedeemed';
    case CertificateStatusEnum.Expired:
      return 'expired';
    case CertificateStatusEnum.Redeemed:
      return 'redeemed';
    case CertificateStatusEnum.Pending:
      return 'pending';
    default:
      return 'neutral';
  }
}

export function getWalletStatusLabel(status: CertificateStatusEnum, intl: IntlShape): string {
  if (status === CertificateStatusEnum.PartiallyRedeemed) {
    return intl.formatMessage({ id: 'certificates.wallet.status.partiallyRedeemed' });
  }
  return status;
}

export interface WalletItemDisplayParams {
  item: ICertificateItem;
  certificateStatus: CertificateStatusEnum;
  certificateExpiresAt: string;
  certificateUpdatedAt?: string;
}

export function getWalletItemState(
  params: WalletItemDisplayParams,
): 'available' | 'used' | 'expired' | 'canceled' | 'pending' {
  const { item, certificateStatus } = params;
  if (
    certificateStatus === CertificateStatusEnum.Expired ||
    item.status === CertificateStatusEnum.Expired
  ) {
    return 'expired';
  }
  if (certificateStatus === CertificateStatusEnum.Canceled) {
    return 'canceled';
  }
  if (certificateStatus === CertificateStatusEnum.Pending) {
    return 'pending';
  }
  return item.status === CertificateStatusEnum.Redeemed ? 'used' : 'available';
}

export function getWalletItemStatusText(params: WalletItemDisplayParams, intl: IntlShape): string {
  const { item, certificateStatus, certificateExpiresAt, certificateUpdatedAt } = params;
  if (
    certificateStatus === CertificateStatusEnum.Expired ||
    item.status === CertificateStatusEnum.Expired
  ) {
    const dateText: string = formatWalletDate(certificateExpiresAt);
    return dateText
      ? intl.formatMessage({ id: 'certificates.wallet.status.expiredOn' }, { date: dateText })
      : intl.formatMessage({ id: 'certificates.wallet.status.expired' });
  }
  if (certificateStatus === CertificateStatusEnum.Canceled && certificateUpdatedAt) {
    const dateText: string = formatWalletDate(certificateUpdatedAt);
    return dateText
      ? intl.formatMessage({ id: 'certificates.wallet.status.canceledOn' }, { date: dateText })
      : intl.formatMessage({ id: 'certificates.wallet.status.canceled' });
  }
  if (item.status === CertificateStatusEnum.Redeemed) {
    const dateText: string = item.updatedAt ? formatWalletDate(item.updatedAt) : '';
    return dateText
      ? intl.formatMessage({ id: 'certificates.wallet.status.usedOn' }, { date: dateText })
      : intl.formatMessage({ id: 'certificates.wallet.status.redeemed' });
  }
  if (certificateStatus === CertificateStatusEnum.Pending) {
    return intl.formatMessage({ id: 'certificates.wallet.status.pending' });
  }
  return intl.formatMessage({ id: 'certificates.wallet.status.available' });
}

export function getWalletItemActionText(params: WalletItemDisplayParams, intl: IntlShape): string {
  const { item, certificateStatus } = params;
  if (
    certificateStatus === CertificateStatusEnum.Expired ||
    item.status === CertificateStatusEnum.Expired
  ) {
    return intl.formatMessage({ id: 'certificates.wallet.status.expired' });
  }
  if (certificateStatus === CertificateStatusEnum.Canceled) {
    return intl.formatMessage({ id: 'certificates.wallet.status.canceled' });
  }
  if (certificateStatus === CertificateStatusEnum.Pending) {
    return intl.formatMessage({ id: 'certificates.wallet.status.preview' });
  }
  return item.status === CertificateStatusEnum.Redeemed
    ? intl.formatMessage({ id: 'certificates.wallet.status.redeemed' })
    : intl.formatMessage({ id: 'certificates.wallet.redeem' });
}

export function isWalletItemActionDisabled(item: ICertificateItem, certificateStatus: CertificateStatusEnum): boolean {
  return (
    (item.status !== CertificateStatusEnum.Active &&
    item.status !== CertificateStatusEnum.PartiallyRedeemed &&
    item.status !== CertificateStatusEnum.Pending) ||
    certificateStatus === CertificateStatusEnum.Expired ||
    certificateStatus == CertificateStatusEnum.Canceled
  );
}

export function getWalletItemIcon(item: ICertificateItem): string {
  const key: string = String(item.type).toLowerCase();
  return ITEM_ICON_BY_TYPE[key] ?? HotelIcon;
}

export function formatWalletDate(value: string): string {
  if (!value) {
    return '';
  }
  const date: Date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

/** API may send consistent lowercase; normalize defensively. */
function normalizeInstalmentEntryStatus(status: string): InstalmentScheduleItemStatus {
  const lower = status.toLowerCase();
  if (lower === 'paid') {
    return 'paid';
  }
  if (lower === 'failed') {
    return 'failed';
  }
  return 'scheduled';
}

function formatInstalmentTooltipDateLine(isoDate: string): string {
  const date: Date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

function mapScheduleStatusToUiStatus(
  status: InstalmentScheduleItemStatus,
): InstallmentSegmentUiStatus {
  switch (status) {
    case 'paid':
      return 'paid';
    case 'failed':
      return 'failed';
    default:
      return 'scheduled';
  }
}

function getInstalmentSegmentStatusLabel(
  status: InstalmentScheduleItemStatus,
  intl: IntlShape,
): string {
  switch (status) {
    case 'paid':
      return intl.formatMessage({ id: 'certificates.wallet.instalment.segmentStatus.paid' });
    case 'failed':
      return intl.formatMessage({ id: 'certificates.wallet.instalment.segmentStatus.failed' });
    default:
      return intl.formatMessage({ id: 'certificates.wallet.instalment.segmentStatus.scheduled' });
  }
}

/** Next payment due: first instalment row (by `number`) with status scheduled or failed. */
function resolveNextPaymentDateFromSchedule(
  instalmentSchedule: IInstalmentSchedule | undefined,
): string {
  const scheduleEntries: readonly IInstalmentScheduleEntry[] | undefined =
    instalmentSchedule?.instalments;
  if (!scheduleEntries?.length) {
    return '';
  }
  const sorted: IInstalmentScheduleEntry[] = [...scheduleEntries].sort(
    (a, b) => a.number - b.number,
  );
  const next: IInstalmentScheduleEntry | undefined = sorted.find((entry) => {
    const status: InstalmentScheduleItemStatus = normalizeInstalmentEntryStatus(entry.status);
    return status === 'scheduled' || status === 'failed';
  });
  return next ? formatWalletDate(next.date) : '';
}

export function resolveInstallmentProgressProps(args: {
  certificate: IUserCertificate;
  instalmentSchedule: IInstalmentSchedule | undefined;
  intl: IntlShape;
  /** When instalments were loaded via certificate-with-installments, prefer this `totalPrice`. */
  totalPriceFromInstallmentsResponse?: number;
}): InstallmentProgressData | null {
  const { certificate, instalmentSchedule, intl, totalPriceFromInstallmentsResponse } = args;
  if (!isInstalmentWalletPaymentType(certificate.paymentType)) {
    return null;
  }

  const scheduleWrapper: IInstalmentSchedule | undefined = instalmentSchedule;
  const scheduleEntries: readonly IInstalmentScheduleEntry[] | undefined =
    scheduleWrapper?.instalments;
  if (!scheduleWrapper || !scheduleEntries?.length) {
    return null;
  }

  const formatMoney = formatMoneyCurrency('USD');
  const totalPrice: number = totalPriceFromInstallmentsResponse ?? certificate.totalPrice;
  const totalFormatted: string = formatMoney.format(totalPrice);

  const sorted: IInstalmentScheduleEntry[] = [...scheduleEntries].sort(
    (a, b) => a.number - b.number,
  );
  const totalSegmentCount: number =
    scheduleWrapper.totalInstalments > 0 ? scheduleWrapper.totalInstalments : sorted.length;

  const segments: InstallmentProgressSegment[] = sorted.map((entry: IInstalmentScheduleEntry) => {
    const normalizedStatus: InstalmentScheduleItemStatus = normalizeInstalmentEntryStatus(
      entry.status,
    );
    return {
      installmentNumber: entry.number,
      amountFormatted: formatMoney.format(entry.amount),
      segmentStatus: mapScheduleStatusToUiStatus(normalizedStatus),
      statusLabel: getInstalmentSegmentStatusLabel(normalizedStatus, intl),
      dateFormatted: formatInstalmentTooltipDateLine(entry.date),
    };
  });

  let paidAmountNumeric: number = sorted
    .filter((entry) => normalizeInstalmentEntryStatus(entry.status) === 'paid')
    .reduce((sum, entry) => sum + entry.amount, 0);
  if (paidAmountNumeric === 0 && typeof scheduleWrapper.remainingBalance === 'number') {
    paidAmountNumeric = Math.max(0, totalPrice - scheduleWrapper.remainingBalance);
  }

  const paidFormatted: string = formatMoney.format(Math.min(paidAmountNumeric, totalPrice));
  const completedCount: number = segments.filter(
    (segment) => segment.segmentStatus === 'paid',
  ).length;
  const nextPaymentDateFormatted: string = resolveNextPaymentDateFromSchedule(scheduleWrapper);

  return {
    paidAmountFormatted: paidFormatted,
    totalAmountFormatted: totalFormatted,
    completedCount,
    totalSegmentCount,
    segments,
    nextPaymentDateFormatted,
  };
}

export function shouldShowWalletUpdatePaymentCard(certificate: {
  paymentType: string;
  status: CertificateStatusEnum;
}): boolean {
  if (!isInstalmentWalletPaymentType(certificate.paymentType)) {
    return false;
  }
  return certificate.status === CertificateStatusEnum.Pending;
}

export function shouldShowExpiryOnFullPaymentNote(args: {
  expiresText: string;
  paymentType: string;
  status: CertificateStatusEnum;
}): boolean {
  const { expiresText, paymentType, status } = args;
  if (expiresText) {
    return false;
  }
  if (status !== CertificateStatusEnum.Pending) {
    return false;
  }
  return isInstalmentWalletPaymentType(paymentType);
}

export { certificateHasFailedInstalmentPayment, instalmentScheduleHasFailedPayment };
